import React, { useState, useEffect } from "react";

const BreakEvenCalculator = () => {
  const [fixedCosts, setFixedCosts] = useState(500000);
  const [variableCostPerUnit, setVariableCostPerUnit] = useState(200);
  const [sellingPricePerUnit, setSellingPricePerUnit] = useState(500);
  const [targetProfit, setTargetProfit] = useState(0);
  const [currentSales, setCurrentSales] = useState(2000);
  const [periodType, setPeriodType] = useState("monthly");
  const [result, setResult] = useState({
    breakEvenUnits: "0",
    breakEvenRevenue: "0.00",
    contributionMargin: "0.00",
    contributionMarginRatio: "0.00",
    profitAtCurrentSales: "0.00",
    safetyMargin: "0.00",
    safetyMarginPercent: "0.00",
    unitsForTargetProfit: "0",
    businessStatus: "",
  });

  // Break-Even Calculation Function
  const calculateBreakEven = () => {
    const fixed = parseFloat(fixedCosts);
    const variableCost = parseFloat(variableCostPerUnit);
    const sellingPrice = parseFloat(sellingPricePerUnit);
    const profit = parseFloat(targetProfit);
    const sales = parseFloat(currentSales);

    if (isNaN(fixed) || isNaN(variableCost) || isNaN(sellingPrice) || fixed < 0 || variableCost < 0 || sellingPrice <= 0) return;

    // Check if selling price is greater than variable cost
    if (sellingPrice <= variableCost) {
      setResult({
        breakEvenUnits: "∞",
        breakEvenRevenue: "∞",
        contributionMargin: (sellingPrice - variableCost).toFixed(2),
        contributionMarginRatio: "0.00",
        profitAtCurrentSales: "N/A",
        safetyMargin: "N/A",
        safetyMarginPercent: "0.00",
        unitsForTargetProfit: "∞",
        businessStatus: "Not Viable - Price too low",
      });
      return;
    }

    const contributionMargin = sellingPrice - variableCost;
    const contributionMarginRatio = (contributionMargin / sellingPrice) * 100;
    
    // Break-even units
    const breakEvenUnits = Math.ceil(fixed / contributionMargin);
    const breakEvenRevenue = breakEvenUnits * sellingPrice;
    
    // Units for target profit
    const unitsForTargetProfit = Math.ceil((fixed + profit) / contributionMargin);
    
    // Profit at current sales
    const profitAtCurrentSales = (sales * contributionMargin) - fixed;
    
    // Margin of safety
    const safetyMargin = sales - breakEvenUnits;
    const safetyMarginPercent = sales > 0 ? (safetyMargin / sales) * 100 : 0;
    
    let businessStatus = "";
    if (sales < breakEvenUnits) {
      businessStatus = "Loss Making";
    } else if (sales === breakEvenUnits) {
      businessStatus = "At Break-Even";
    } else if (safetyMarginPercent < 20) {
      businessStatus = "Low Safety Margin";
    } else if (safetyMarginPercent < 40) {
      businessStatus = "Moderate Safety";
    } else {
      businessStatus = "Healthy Profit Zone";
    }

    setResult({
      breakEvenUnits: breakEvenUnits.toString(),
      breakEvenRevenue: breakEvenRevenue.toFixed(2),
      contributionMargin: contributionMargin.toFixed(2),
      contributionMarginRatio: contributionMarginRatio.toFixed(2),
      profitAtCurrentSales: profitAtCurrentSales.toFixed(2),
      safetyMargin: safetyMargin.toFixed(0),
      safetyMarginPercent: safetyMarginPercent.toFixed(2),
      unitsForTargetProfit: unitsForTargetProfit.toString(),
      businessStatus,
    });
  };

  useEffect(() => {
  calculateBreakEven();
}, [fixedCosts, variableCostPerUnit, sellingPricePerUnit, targetProfit, currentSales, periodType]);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
            </svg>
          </div>
          <h1>Break-Even Point Calculator</h1>
          <p>
            Calculate how many units you need to sell to cover costs and start making profit
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Cost & Revenue Details
              </h2>

              {/* Fixed Costs */}
              <div className="input-group">
                <label>Fixed Costs (Rent, Salaries, etc.)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={fixedCosts}
                    onChange={(e) => setFixedCosts(e.target.value)}
                    className="calc-input"
                    placeholder="500000"
                  />
                </div>
                <div className="quick-select-group">
                  {[200000, 500000, 1000000, 2000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setFixedCosts(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Variable Cost Per Unit */}
              <div className="input-group">
                <label>Variable Cost Per Unit</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={variableCostPerUnit}
                    onChange={(e) => setVariableCostPerUnit(e.target.value)}
                    className="calc-input"
                    placeholder="200"
                  />
                </div>
                <div className="quick-select-group">
                  {[100, 200, 300, 500].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setVariableCostPerUnit(amount)}
                      className="quick-select-btn secondary"
                    >
                      ₹{amount}
                    </button>
                  ))}
                </div>
              </div>

              {/* Selling Price Per Unit */}
              <div className="input-group">
                <label>Selling Price Per Unit</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={sellingPricePerUnit}
                    onChange={(e) => setSellingPricePerUnit(e.target.value)}
                    className="calc-input"
                    placeholder="500"
                  />
                </div>
                <div className="quick-select-group">
                  {[300, 500, 750, 1000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setSellingPricePerUnit(amount)}
                      className="quick-select-btn success"
                    >
                      ₹{amount}
                    </button>
                  ))}
                </div>
              </div>

              {/* Period Type */}
              <div className="input-group">
                <label>Analysis Period</label>
                <div className="quick-select-group">
                  {[
                    { value: "monthly", label: "Monthly" },
                    { value: "annual", label: "Annual" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setPeriodType(type.value)}
                      className={`quick-select-btn ${periodType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Current Sales Volume */}
              <div className="input-group">
                <label>Current Sales Volume (Units)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={currentSales}
                    onChange={(e) => setCurrentSales(e.target.value)}
                    className="calc-input"
                    placeholder="2000"
                  />
                  <span className="input-suffix">units</span>
                </div>
                <div className="quick-select-group">
                  {[1000, 2000, 5000, 10000].map((units) => (
                    <button
                      key={units}
                      onClick={() => setCurrentSales(units)}
                      className="quick-select-btn success"
                    >
                      {units >= 1000 ? `${(units / 1000).toFixed(0)}K` : units}
                    </button>
                  ))}
                </div>
              </div>

              {/* Target Profit */}
              <div className="input-group">
                <label>Target Profit (Optional)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={targetProfit}
                    onChange={(e) => setTargetProfit(e.target.value)}
                    className="calc-input"
                    placeholder="0"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 100000, 250000, 500000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setTargetProfit(amount)}
                      className="quick-select-btn secondary"
                    >
                      {amount === 0 ? "₹0" : `₹${(amount / 100000).toFixed(0)}L`}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateBreakEven} className="calculate-btn">
                Calculate Break-Even
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Break-Even Analysis
              </h2>

              <div className="result-card">
                {/* Break-Even Units - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Break-Even Point</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.breakEvenUnits === "∞" ? "∞" : `${Number(result.breakEvenUnits).toLocaleString("en-IN")} units`}
                  </div>
                  <div className="result-subtext">
                    {result.businessStatus}
                  </div>
                </div>

                {/* Break-Even Revenue */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Break-Even Revenue</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.breakEvenRevenue === "∞" ? "∞" : `₹${Number(result.breakEvenRevenue).toLocaleString("en-IN")}`}
                  </div>
                  <div className="result-subtext">
                    Minimum revenue needed
                  </div>
                </div>

                {/* Contribution Margin */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Contribution Margin</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                        <polyline points="17 6 23 6 23 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.contributionMargin).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.contributionMarginRatio}% per unit
                  </div>
                </div>

                {/* Current Profit/Loss */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Current Profit/Loss</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.profitAtCurrentSales === "N/A" ? "N/A" : `₹${Number(result.profitAtCurrentSales).toLocaleString("en-IN")}`}
                  </div>
                  <div className="result-subtext">
                    At {Number(currentSales).toLocaleString("en-IN")} units
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Sales Analysis</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Break-Even Point</span>
                      <span className="breakdown-percentage">
                        {result.breakEvenUnits === "∞" ? "∞" : `${Number(result.breakEvenUnits).toLocaleString("en-IN")} units`}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: result.breakEvenUnits === "∞" ? "100%" : `${Math.min((parseFloat(result.breakEvenUnits) / currentSales) * 100, 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Current Sales</span>
                      <span className="breakdown-percentage">
                        {Number(currentSales).toLocaleString("en-IN")} units
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: "100%",
                        }}
                      />
                    </div>
                  </div>
                  {parseFloat(targetProfit) > 0 && (
                    <div className="breakdown-item">
                      <div className="breakdown-header">
                        <span className="breakdown-label">Target Profit Sales</span>
                        <span className="breakdown-percentage">
                          {result.unitsForTargetProfit === "∞" ? "∞" : `${Number(result.unitsForTargetProfit).toLocaleString("en-IN")} units`}
                        </span>
                      </div>
                      <div className="breakdown-bar">
                        <div
                          className="breakdown-bar-fill blue"
                          style={{
                            width: result.unitsForTargetProfit === "∞" ? "100%" : `${Math.min((parseFloat(result.unitsForTargetProfit) / currentSales) * 100, 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Safety Margin Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>Margin of Safety</h3>
                  <p style={{fontSize: '1.25rem', fontWeight: 'bold', color: '#047857', margin: '0.5rem 0'}}>
                    {result.safetyMargin === "N/A" ? "N/A" : `${Number(result.safetyMargin).toLocaleString("en-IN")} units (${result.safetyMarginPercent}%)`}
                  </p>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    {parseFloat(result.safetyMarginPercent) > 0 
                      ? "This is your cushion above break-even. Higher is better for business safety."
                      : "You're below break-even. Increase sales or reduce costs to become profitable."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Break-Even Point</h3>
              <p>
                Break-even point is the sales volume at which total revenue equals total costs, resulting in zero profit 
                or loss. It's a critical metric for businesses to determine the minimum sales needed to cover all expenses. 
                Below this point, the business operates at a loss; above it, the business generates profit. The calculation 
                considers fixed costs (rent, salaries, insurance that don't change with production) and variable costs 
                (materials, direct labor that increase with each unit). The difference between selling price and variable 
                cost per unit is the contribution margin - the amount each unit contributes toward covering fixed costs. 
                Once fixed costs are covered, all additional contribution becomes profit. Understanding your break-even 
                point helps in pricing decisions, cost control, sales target setting, and evaluating business viability. 
                The margin of safety shows how much sales can drop before reaching break-even, indicating business risk level.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Break-Even Formula:</h4>
            <div className="formula-display">
              Break-Even Units = Fixed Costs ÷ (Selling Price - Variable Cost per Unit)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Fixed Costs</div>
                <div className="formula-param-value">Costs that don't vary with production volume</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Variable Cost</div>
                <div className="formula-param-value">Costs that change with each unit produced</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Contribution Margin</div>
                <div className="formula-param-value">Selling Price - Variable Cost per unit</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Margin of Safety</div>
                <div className="formula-param-value">Current Sales - Break-Even Sales</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What are fixed costs and variable costs in business?</h5>
              <p>
                Fixed costs remain constant regardless of production or sales volume. Common fixed costs include monthly 
                rent or lease payments, permanent employee salaries, insurance premiums, depreciation on equipment, annual 
                licenses and permits, property taxes, and loan interest payments. These costs must be paid even if you 
                produce zero units. Variable costs change in direct proportion to production volume. Examples include raw 
                materials, packaging supplies, direct labor (piece-rate workers), shipping and freight charges, sales 
                commissions, utility costs tied to production, and credit card processing fees. If you produce 100 units, 
                you incur variable costs for 100 units; produce 200 units, variable costs double. Some costs are semi-variable 
                (e.g., utilities with base charges plus usage).
              </p>
            </div>

            <div className="faq-item">
              <h5>How can I lower my break-even point?</h5>
              <p>
                Lower your break-even point through four strategies. First, reduce fixed costs: negotiate lower rent, 
                downsize office space, outsource non-core functions instead of hiring full-time staff, use equipment leasing 
                instead of purchasing, switch to variable expense structures where possible, or share facilities with other 
                businesses. Second, decrease variable costs: negotiate better supplier rates through bulk purchasing, improve 
                production efficiency to reduce material waste, automate repetitive processes, find alternative suppliers, 
                or optimize logistics and shipping. Third, increase selling prices: add value through better features or 
                service, strengthen brand positioning to justify premium pricing, create product bundles, or target less 
                price-sensitive market segments.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is contribution margin and why does it matter?</h5>
              <p>
                Contribution margin is the selling price minus variable cost per unit - the amount each sale contributes 
                toward covering fixed costs and generating profit. If a product sells for ₹500 with ₹200 variable cost, 
                contribution margin is ₹300 (60% contribution margin ratio). This ₹300 first goes toward covering fixed 
                costs; once those are covered, it becomes pure profit. Contribution margin is crucial for multiple decisions. 
                For pricing: knowing your contribution margin helps set minimum acceptable prices. For product mix: prioritize 
                products with higher contribution margins. For special orders: accept orders above variable cost if you have 
                excess capacity, as they contribute to fixed costs. For cost control: identify which costs are variable vs. 
                fixed to focus reduction efforts.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is margin of safety and what's considered healthy?</h5>
              <p>
                Margin of safety measures how much sales can decline before reaching break-even point. It's calculated as 
                (Current Sales - Break-Even Sales) ÷ Current Sales × 100. For example, if you sell 2,000 units with break-even 
                at 1,500 units, margin of safety is 500 units or 25%. This represents your cushion against sales fluctuations. 
                A healthy margin of safety is typically 30-40% or higher, indicating strong profitability with good protection 
                against market downturns. Below 20% is risky - minor sales drops could push you into losses. Negative margin 
                means you're operating below break-even at a loss. Industries with stable demand can operate with lower margins 
                of safety, while seasonal or volatile businesses need higher margins. Improve margin of safety by increasing 
                sales volume, raising prices, reducing costs, or ideally combining all three. 
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I use break-even analysis for pricing decisions?</h5>
              <p>
                Break-even analysis is fundamental for pricing strategy. Start by calculating your total cost per unit 
                (variable cost + allocated fixed cost based on expected volume). This is your absolute minimum price - 
                selling below this creates losses. However, in the short term with excess capacity, you can accept prices 
                above variable cost since they contribute to fixed costs already being paid. For new product pricing, estimate 
                realistic sales volumes at different price points, calculate break-even for each scenario, and assess which 
                offers best profit potential and market competitiveness. Consider volume-price trade-offs: lower prices 
                increase volume but require higher break-even units; premium pricing offers higher contribution per unit 
                but may limit volume. Test different scenarios: if you raise prices 10% but lose 5% volume, are you better 
                off? Calculate break-even impact.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BreakEvenCalculator;